import { DocumentNode } from 'graphql';
import { Edit, Trash } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { GetParticipantDataQuery as GetParticipantData, Permission } from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../../../common/Common/OptionsDropdown';
import { assetUrl } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import { useSession } from '../../../Auth/SessionProvider';
import DeleteNoteMutation from '../About/DeleteNoteMutation';
import GetParticipantDataQuery from '../GetParticipantDataQuery';
import UI from '../../../../common/UI';
import useAddNoteModal from '../About/useAddNoteModal';
import useConfirmModal from '../../../../common/useConfirmModal';
import useEditNoteModal from '../About/useEditNoteModal';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';

type Note = GetParticipantData['project']['participant']['notes'][0];

export interface NotesPageProps {
  participantId: string;
}

const NotesPage = ({ participantId }: NotesPageProps) => {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const project = useProject();

  const hasEditPermission = hasPermission(project.id, [Permission.edit_registrations]);

  const [openAddNoteModal] = useAddNoteModal(participantId);

  const { data } = useQuery(
    GetParticipantDataQuery,
    {
      variables: {
        project_id: project.id,
        participant_id: participantId,
      },
    },
  );

  const participant = data?.project.participant;

  if (!participant) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
          <UI.SectionHeading>
            {t('notes')}
          </UI.SectionHeading>

          {hasEditPermission && participant.active && participant.notes.length > 0 && (
            <UI.Button
              onClick={() => openAddNoteModal()}
              sc={{ brand: 'secondary' }}
            >
              {t('add_note')}
            </UI.Button>
          )}
        </UI.FlexContainer>

        {participant.notes.length > 0 && (
          <UI.GridContainer sc={{ gutter: 0.5 }}>
            {participant.notes.map((note) => (
              <NoteCard note={note} key={note.id} />
            ))}
          </UI.GridContainer>
        )}

        {participant.notes.length === 0 && (
          <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }} style={{ textWrap: 'balance' }}>
            <UI.Div>
              <img src={assetUrl('images/communicate.svg')} alt="" height={120} />
            </UI.Div>
            <UI.Paragraph>{t('no_notes_description')}</UI.Paragraph>
            {hasEditPermission && participant.active && (
              <UI.Button
                onClick={() => openAddNoteModal()}
                sc={{ brand: 'secondary.500' }}
              >
                {t('add_note')}
              </UI.Button>
            )}
          </UI.Card>
        )}
      </UI.GridContainer>
    </UI.Section>
  );
};

interface NoteCardProps {
  note: Note;
  refetchQueries?: DocumentNode[];
}

const NoteCard = ({ note, refetchQueries = [GetParticipantDataQuery] }: NoteCardProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const { hasPermission } = useSession();
  const project = useProject();
  const notifier = useNotifier();

  const [openEditNoteModal] = useEditNoteModal(note);

  const [deleteNote] = useMutation(DeleteNoteMutation, {
    variables: {
      id: note.id,
    },
    refetchQueries,
    awaitRefetchQueries: true,
    onCompleted: () => {
      notifier.success(t('notifications.note_deleted'));
    },
  });

  const [confirmDelete] = useConfirmModal({
    title: t('delete_note'),
    body: t('delete_note_description'),
    onConfirm: () => deleteNote(),
  }, [note]);

  const hasEditPermission = hasPermission(project.id, [Permission.edit_registrations]);

  return (
    <UI.Card sc={{ hasSidePanel: true }}>
      <UI.Div>
        <UI.GridContainer
          sc={{ columns: '36px 1fr', gutter: 0.5 }}
        >
          <UI.Div sc={{ pt: 2 / 3 }}>
            <UI.ProfilePic sc={{ size: 36 }}>
              <UI.Span><UI.Span>{(note.user.full_name || note.user.email).charAt(0).toUpperCase()}</UI.Span></UI.Span>
            </UI.ProfilePic>
          </UI.Div>
          <UI.Div>
            <UI.Div sc={{ strong: true }}>
              {note.user.full_name || note.user.email}
            </UI.Div>
            <UI.Div sc={{ muted: true }}>
              {formatDate(
                parseDate(note.created_at, { timezone: 'UTC' }),
                { format: 'display_date_time' },
              )}
            </UI.Div>
          </UI.Div>
        </UI.GridContainer>
      </UI.Div>
      <UI.Div>
        <ContainerWithOptions
          renderOptions={hasEditPermission ? () => (
            <>
              <Option
                icon={<Edit />}
                label={t('edit')}
                onClick={() => openEditNoteModal()}
              />
              <Option
                icon={<Trash />}
                label={t('delete')}
                onClick={() => confirmDelete()}
              />
            </>
          ) : undefined}
        >
          <UI.NL2BR content={note.content} />
        </ContainerWithOptions>
      </UI.Div>
    </UI.Card>
  );
};

export default NotesPage;
